import React, { useState, useRef, useEffect } from "react";
import { Input, Button } from "reactstrap";

// Escaneo con handheld (lector tipo teclado: escribe el codigo y manda Enter o Tab), en Recepciones y
// Surtir pedido. Se escanea el articulo UNA vez y se captura la cantidad (viene con lo que falta).
// Un codigo se reconoce por el SKU, el EAN, el codigo, el ASIN, la MLM, el ID de TikTok o el id interno
// (QR del sistema), sin importar mayusculas ni espacios. Los avisos NO usan ventanas (le quitarian el
// foco al campo y el siguiente escaneo se perderia): pitido + vibracion + texto.
export const normCodigo = (t) => String(t == null ? "" : t).trim().toUpperCase();

export const codigosDe = (art) =>
  art
    ? [art._id, art.SKU, art.EAN, art.codigo, art.ASIN, art.MLM, ...String(art.skuTikTok || "").split(",")]
        .map(normCodigo)
        .filter((c) => c && c !== "NA")
    : [];

// Renglon de ese codigo que todavia no llega a su tope. renglones: [{ codigos: [...], cantidad, tope }]
export function localizarEscaneo(renglones, codigo) {
  const c = normCodigo(codigo);
  const delCodigo = renglones.filter((r) => (r.codigos || []).includes(c));
  if (!delCodigo.length) return { aviso: `${codigo}: no viene en esta lista` };
  const destino = delCodigo.find((r) => (parseFloat(r.cantidad) || 0) < (parseFloat(r.tope) || 0));
  if (!destino) return { aviso: `${codigo}: ya está completo (${delCodigo[0].tope})` };
  return { destino, restante: (parseFloat(destino.tope) || 0) - (parseFloat(destino.cantidad) || 0) };
}

// Suma `cantidad` (sin pasar del tope) al renglon de ese codigo. Regresa { renglones, aviso, renglon }.
export function sumarEscaneo(renglones, codigo, cantidad = 1) {
  const { destino, restante, aviso } = localizarEscaneo(renglones, codigo);
  if (aviso) return { renglones, aviso };
  const n = Math.min(parseFloat(cantidad) || 0, restante);
  const nuevo = { ...destino, cantidad: (parseFloat(destino.cantidad) || 0) + n };
  return { renglones: renglones.map((r) => (r === destino ? nuevo : r)), aviso: null, renglon: nuevo };
}

// Pitido corto (ok) o doble y grave (error) + vibracion si el equipo la tiene
export function senalEscaneo(ok) {
  try {
    if (navigator.vibrate) navigator.vibrate(ok ? 40 : [120, 60, 120]);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tono = (frec, inicio, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = frec;
      g.gain.value = 0.15;
      o.connect(g);
      g.connect(ctx.destination);
      o.start(ctx.currentTime + inicio);
      o.stop(ctx.currentTime + inicio + dur);
    };
    if (ok) tono(1400, 0, 0.08);
    else {
      tono(300, 0, 0.15);
      tono(300, 0.22, 0.15);
    }
    setTimeout(() => ctx.close(), 700);
  } catch (e) {
    // sin audio: solo el texto
  }
}

// Campo del lector en dos pasos: 1) se escanea el codigo; 2) se captura la cantidad (viene con lo que
// falta y seleccionada: Enter acepta, o se escribe otra) y vuelve a quedar listo para el siguiente.
//  - renglones: los de la pantalla ({ codigos, cantidad, tope, etiqueta })
//  - onConfirmar(codigo, cantidad): suma esa cantidad (la pantalla usa sumarEscaneo)
export function CampoEscaneo({ renglones, onConfirmar, placeholder }) {
  const [codigo, setCodigo] = useState("");
  const [pendiente, setPendiente] = useState(null); // { codigo, etiqueta, restante }
  const [cantidad, setCantidad] = useState("");
  const [aviso, setAviso] = useState("");
  const [ultimo, setUltimo] = useState("");
  const refCodigo = useRef(null);
  const refCantidad = useRef(null);

  const enfocarCodigo = () => setTimeout(() => refCodigo.current && refCodigo.current.focus(), 0);
  // con llaves: si el efecto regresara el id del setTimeout, React lo llamaria al desmontar
  // (al pasar de Escaneando a Manual) y truena con "func.apply is not a function"
  useEffect(() => {
    enfocarCodigo();
  }, []);
  useEffect(() => {
    if (pendiente && refCantidad.current) {
      refCantidad.current.focus();
      refCantidad.current.select();
    }
  }, [pendiente]);

  const leerCodigo = (v) => {
    const r = localizarEscaneo(renglones, v);
    if (r.aviso) {
      setAviso(r.aviso);
      setUltimo("");
      senalEscaneo(false);
      enfocarCodigo();
      return;
    }
    setAviso("");
    setPendiente({ codigo: v, etiqueta: r.destino.etiqueta || v, restante: r.restante });
    setCantidad(String(r.restante));
    senalEscaneo(true);
  };

  const confirmar = () => {
    const n = parseFloat(cantidad);
    if (!(n > 0) || n > pendiente.restante) {
      setAviso(`${pendiente.etiqueta}: captura de 1 a ${pendiente.restante}`);
      senalEscaneo(false);
      setCantidad(String(pendiente.restante));
      setTimeout(() => refCantidad.current && refCantidad.current.select(), 0);
      return;
    }
    onConfirmar(pendiente.codigo, n);
    setUltimo(`${pendiente.etiqueta}: +${n}`);
    setAviso("");
    setPendiente(null);
    setCantidad("");
    enfocarCodigo();
  };

  const cancelar = () => {
    setPendiente(null);
    setCantidad("");
    setAviso("");
    enfocarCodigo();
  };

  return (
    <div>
      {!pendiente ? (
        <Input
          innerRef={refCodigo}
          value={codigo}
          inputMode="none"
          autoComplete="off"
          placeholder={placeholder || "Escanea el código"}
          onChange={(e) => setCodigo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              const v = codigo.trim();
              setCodigo("");
              if (v) leerCodigo(v);
            }
          }}
          style={{ fontSize: "1.1rem", borderColor: "#6366f1", boxShadow: "0 0 0 2px #e0e7ff" }}
        />
      ) : (
        <div style={{ border: "2px solid #6366f1", borderRadius: 8, padding: 8, background: "#eef2ff" }}>
          <div style={{ fontWeight: 700 }}>{pendiente.etiqueta}</div>
          <div style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 4 }}>Faltan {pendiente.restante} · ¿cuántas?</div>
          <div style={{ display: "flex", gap: 6 }}>
            <Input
              innerRef={refCantidad}
              type="number"
              inputMode="numeric"
              min="1"
              step="any"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  confirmar();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelar();
                }
              }}
              style={{ fontSize: "1.3rem", fontWeight: 700, maxWidth: 140 }}
            />
            <Button color="success" onClick={confirmar}>
              <i className="fas fa-check"></i>
            </Button>
            <Button color="secondary" onClick={cancelar}>
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>
      )}
      {aviso ? (
        <div style={{ color: "#b91c1c", fontWeight: 700, marginTop: 4 }}>✖ {aviso}</div>
      ) : ultimo ? (
        <div style={{ color: "#047857", fontWeight: 700, marginTop: 4 }}>✔ {ultimo}</div>
      ) : null}
    </div>
  );
}
